import React, { useState } from "react";
import { toast } from "sonner";
import api, { fmtIDR } from "../lib/api";
import { fmtUSD } from "../lib/currency";
import { useI18n } from "../lib/i18n";

function Line({ label, value, cls = "text-[#F5F3ED]", testId }) {
  return (
    <div className="flex justify-between pt-2 text-sm">
      <span className="text-xs uppercase tracking-widest text-[#A8A8AD]">{label}</span>
      <span data-testid={testId} className={cls}>{value}</span>
    </div>
  );
}

export function OrderItems({ o }) {
  const { t } = useI18n();
  const due = o.amount_due_idr ?? o.total_idr;
  return (
    <div className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
      <div className="text-xs uppercase tracking-widest text-[#A8A8AD] mb-3">{t("order.items")}</div>
      {o.items.map((it, i) => (
        <div key={i} className="flex justify-between py-2 border-b border-[#35353B] last:border-0">
          <div>
            <div className="text-[#F5F3ED]">{it.product_name}</div>
            <div className="text-xs text-[#A8A8AD]">{it.quantity} × {fmtIDR(it.unit_price)}</div>
            {(it.delivered_quantity > 0 || o.deliveries?.length > 0) && (
              <div data-testid={`order-item-progress-${i}`} className="text-xs text-[#35C78A] mt-0.5">{it.delivered_quantity || 0}/{it.quantity} {t("order.delivered")}</div>
            )}
          </div>
          <div className="text-[#D4AF37] font-bold">{fmtIDR(it.subtotal)}</div>
        </div>
      ))}
      <div className="border-t border-[#35353B] mt-2">
        {o.payment_fee_idr > 0 && <Line label="Subtotal" value={fmtIDR(o.total_idr)} testId="order-subtotal" />}
        {o.payment_fee_idr > 0 && <Line label={`Biaya ${o.payment_method} ${o.payment_fee_percent}%`} value={`+ ${fmtIDR(o.payment_fee_idr)}`} testId="order-payment-fee" />}
        <Line label={t("checkout.total")} value={fmtIDR(o.grand_total_idr ?? o.total_idr)} cls="text-xl font-bold text-[#D4AF37]" testId="order-total" />
        {o.balance_used_idr > 0 && <Line label={t("order.balance_used")} value={`- ${fmtIDR(o.balance_used_idr)}`} testId="order-balance-used" />}
        {o.unique_code > 0 && <Line label={t("order.unique_code")} value={`+ ${fmtIDR(o.unique_code)}`} testId="order-unique-code" />}
        {(o.balance_used_idr > 0 || o.unique_code > 0) && <Line label={t("order.amount_due")} value={fmtIDR(due)} cls="font-bold text-[#D4AF37]" testId="order-amount-due-idr" />}
        {o.currency === "USD" && o.fx_rate && (
          <Line label={`${t("order.amount_due")} (${o.payment_asset === "USDT" ? "USDT" : "USD"})`} value={o.payment_asset === "USDT" ? `${Number(o.amount_due_usd).toFixed(2)} USDT · ${t("order.rate")} 1 USDT = ${fmtIDR(o.fx_rate)}` : `${fmtUSD(o.amount_due_usd)} · ${t("order.rate")} $1 = ${fmtIDR(o.fx_rate)}`} cls="text-[#D4AF37] font-bold" testId="order-amount-due-usd" />
        )}
        {o.refund_idr > 0 && <Line label={t("order.refund")} value={`+ ${fmtIDR(o.refund_idr)}`} cls="text-[#35C78A] font-bold" testId="order-refund" />}
      </div>
    </div>
  );
}

export function PartialDeliveryForm({ o, onDone }) {
  const [qtys, setQtys] = useState({});
  const [note, setNote] = useState("");
  const [video, setVideo] = useState("");
  const [busy, setBusy] = useState(false);
  const items = o.items.filter((i) => i.quantity - (i.delivered_quantity || 0) > 0);
  const submit = async () => {
    const payload = items.map((i) => ({ product_id: i.product_id, quantity: Number(qtys[i.product_id] || 0) })).filter((x) => x.quantity > 0);
    if (!payload.length) { toast.error("Isi jumlah yang dikirim"); return; }
    if (!/^https?:\/\/\S+$/.test(video.trim())) { toast.error("URL video bukti pengiriman wajib diisi (http/https)"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/orders/${o.id}/deliveries`, { items: payload, note, video_url: video.trim() });
      toast.success(r.data.completed_delivery ? "Semua item terkirim, status: Dikirimkan" : "Pengiriman bertahap dicatat");
      setQtys({}); setNote(""); setVideo(""); onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="partial-delivery-panel" className="bg-[#202127] border border-[#35353B] rounded-lg p-5">
      <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-1">Pengiriman Bertahap</div>
      <p className="text-xs text-[#A8A8AD] mb-3">Catat jumlah yang sudah dikirim. Jika pesanan dibatalkan, nilai sisa yang belum terkirim otomatis masuk Saldo Store pembeli.</p>
      {items.map((i) => {
        const left = i.quantity - (i.delivered_quantity || 0);
        return (
          <label key={i.product_id} className="flex items-center gap-2 text-sm text-[#F5F3ED] mb-2">
            <span className="flex-1">{i.product_name} <span className="text-xs text-[#A8A8AD]">(sisa {left})</span></span>
            <input data-testid={`partial-qty-${i.product_id}`} type="number" min="0" max={left} step="any" value={qtys[i.product_id] || ""}
              onChange={(e) => setQtys({ ...qtys, [i.product_id]: e.target.value })}
              className="w-24 bg-[#191A1F] border border-[#35353B] rounded-sm px-2 py-1 text-right outline-none focus:border-[#D4AF37]" />
          </label>
        );
      })}
      <input data-testid="partial-note-input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (opsional)"
        className="w-full bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37] mb-2" />
      <input data-testid="partial-video-url-input" type="url" required value={video} maxLength={500} onChange={(e) => setVideo(e.target.value)} placeholder="URL video bukti pengiriman (wajib)"
        className="w-full bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37] mb-2" />
      <button data-testid="partial-submit-btn" disabled={busy} onClick={submit} className="w-full border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#101114] rounded-sm py-2 text-sm transition-colors disabled:opacity-40">Catat Pengiriman Bertahap</button>
    </div>
  );
}
